import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { fonts as fontsApi } from '../api/client.js';

export default function HealthDashboard({ onClose, onSelectFont }) {
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const fetchHealth = () => {
    setLoading(true);
    fontsApi.health()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHealth(); }, []);

  const handleScanAll = async () => {
    setScanning(true);
    try {
      await fontsApi.healthScan();
      fetchHealth();
    } catch (err) {
      console.error('Scan failed:', err);
    } finally {
      setScanning(false);
    }
  };

  const summary = data?.summary || { healthy: 0, warning: 0, error: 0, unchecked: 0 };
  const total = summary.healthy + summary.warning + summary.error + summary.unchecked;

  const cards = [
    { label: 'Healthy', count: summary.healthy, color: '#45B26B', bg: '#45B26B18' },
    { label: 'Warning', count: summary.warning, color: '#F2994A', bg: '#F2994A18' },
    { label: 'Error', count: summary.error, color: '#E85555', bg: '#E8555518' },
    { label: 'Unchecked', count: summary.unchecked, color: '#8B95A5', bg: '#8B95A518' },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 800, maxHeight: '85vh',
          background: theme.bgSecondary, borderRadius: 16,
          border: `1px solid ${theme.border}`, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.text }}>Font Health</h2>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
              {total} fonts in library
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={handleScanAll}
              disabled={scanning}
              style={{
                padding: '8px 16px', fontSize: 12, fontWeight: 600,
                background: scanning ? theme.bgInput : 'linear-gradient(135deg, #4F8EF7, #7C5CFC)',
                border: 'none', borderRadius: 8, color: scanning ? theme.textMuted : '#fff',
                cursor: scanning ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}
            >{scanning ? 'Scanning...' : 'Scan All'}</button>
            <button
              onClick={onClose}
              style={{
                background: theme.bgInput, border: 'none', borderRadius: 8,
                color: theme.textMuted, width: 32, height: 32, cursor: 'pointer', fontSize: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >{'\u2715'}</button>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{
          padding: '16px 24px', borderBottom: `1px solid ${theme.borderLight}`,
          display: 'flex', gap: 12,
        }}>
          {cards.map(c => (
            <div key={c.label} style={{
              flex: 1, padding: '14px 16px', borderRadius: 10,
              background: c.bg, border: `1px solid ${c.color}30`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.count}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: c.color, opacity: 0.8, marginTop: 2 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Font Issues List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 40, color: theme.textMuted, fontSize: 13 }}>
              Loading health data...
            </div>
          )}

          {!loading && (!data?.fonts?.length) && (
            <div style={{ textAlign: 'center', padding: 40, color: theme.textFaint }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>{'\u2713'}</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>All fonts are healthy</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>No issues detected in your font library</div>
            </div>
          )}

          {data?.fonts?.map(font => {
            const issues = typeof font.health_issues === 'string'
              ? JSON.parse(font.health_issues)
              : (font.health_issues || []);
            const isError = font.health_status === 'error';

            return (
              <div key={font.id} style={{
                marginBottom: 12, padding: 14,
                background: theme.bgTertiary, borderRadius: 10,
                border: `1px solid ${isError ? '#E8555530' : '#F2994A30'}`,
                cursor: 'pointer',
              }}
                onClick={() => { onSelectFont?.(font.id); onClose(); }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 8,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>
                    {font.name}
                    <span style={{ fontSize: 11, color: theme.textMuted, fontWeight: 400, marginLeft: 8 }}>
                      {font.family}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    color: isError ? '#E85555' : '#F2994A',
                    background: isError ? '#E8555518' : '#F2994A18',
                    padding: '3px 8px', borderRadius: 6,
                    textTransform: 'uppercase',
                  }}>{font.health_status}</span>
                </div>
                {issues.map((issue, i) => (
                  <div key={i} style={{
                    fontSize: 11, color: theme.textMuted, padding: '2px 0',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{ color: isError ? '#E85555' : '#F2994A' }}>
                      {issue.type === 'warning' ? '\u26A0' : '\u2718'}
                    </span>
                    {issue.message}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
